import React from 'react';
import './Features.css';

const features = [
  {
    title: 'The Onboarding',
    description: 'It begins with an onboarding of 2-4 weeks, not a proposal. A forward-deployed engineer sits inside the company, not to interview executives, but to find out how the business actually runs: the spreadsheet everyone depends on, the subscription no one cancelled, the knowledge that lives in one person\'s head.'
  },
  {
    title: 'The Map',
    description: 'By the end of the week, the picture exists as a live map, and so does something the company has never had before: a full, honest account of every tool it pays for, and what each one is actually worth to it.'
  },
  {
    title: 'The Radar',
    description: 'From the map, a scored backlog builds itself and never stops running. Every internal product worth building, ranked by four numbers: how hard, what it costs, how far it can be shaped to your edge cases, and how much existing spend it replaces.'
  },
  {
    title: 'The Build',
    description: 'New capability first, always. Nothing load-bearing is touched until trust is earned by shipping something small, watching it work, and doing it again. Only then does the radar turn to displacement, replacing the tools you already pay for.'
  }
];

const WeekVisual = () => (
  <div className="viz">
    <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="weekScan" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#9579FC" stopOpacity="0" />
          <stop offset="0.5" stopColor="#9579FC" stopOpacity="0.30" />
          <stop offset="1" stopColor="#9579FC" stopOpacity="0" />
        </linearGradient>
      </defs>

      <text x="150" y="22" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1a">Onboarding, inside the business</text>

      {/* Ambient systems being scanned */}
      <g fill="#E0DACF">
        <rect x="44" y="54" width="18" height="18" rx="5" />
        <rect x="244" y="56" width="18" height="18" rx="5" />
        <rect x="146" y="132" width="18" height="18" rx="5" />
        <rect x="214" y="120" width="18" height="18" rx="5" />
      </g>

      {/* Scan sweep */}
      <rect x="0" y="30" width="42" height="154" fill="url(#weekScan)">
        <animateTransform attributeName="transform" type="translate" values="0 0; 258 0; 0 0" dur="4.5s" repeatCount="indefinite" />
      </rect>

      {/* Discovery flags */}
      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.15;0.25;0.85;1" dur="4.5s" begin="0.4s" repeatCount="indefinite" />
        <rect x="40" y="92" width="140" height="24" rx="12" fill="#ffffff" stroke="#E0E0E0" strokeWidth="1" />
        <circle cx="54" cy="104" r="4" fill="#9579FC" />
        <text x="66" y="108" fontSize="10" fill="#1a1a1a" fontWeight="500">Shadow spreadsheet</text>
      </g>

      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.30;0.40;0.9;1" dur="4.5s" begin="0.4s" repeatCount="indefinite" />
        <rect x="150" y="58" width="120" height="24" rx="12" fill="#ffffff" stroke="#E0E0E0" strokeWidth="1" />
        <circle cx="164" cy="70" r="4" fill="#9579FC" />
        <text x="176" y="74" fontSize="10" fill="#1a1a1a" fontWeight="500">Unused license</text>
      </g>

      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.45;0.55;0.95;1" dur="4.5s" begin="0.4s" repeatCount="indefinite" />
        <rect x="66" y="150" width="150" height="24" rx="12" fill="#ffffff" stroke="#E0E0E0" strokeWidth="1" />
        <circle cx="80" cy="162" r="4" fill="#9579FC" />
        <text x="92" y="166" fontSize="10" fill="#1a1a1a" fontWeight="500">Owned by one person</text>
      </g>
    </svg>
  </div>
);

const MapVisual = () => {
  const tools = [
    { x: 24, y: 50, name: 'CRM', worth: '$2.4k/mo' },
    { x: 202, y: 50, name: 'Billing', worth: '$1.8k/mo' },
    { x: 24, y: 148, name: 'Support', worth: '$3.2k/mo' },
    { x: 202, y: 148, name: 'Docs', worth: '$0.9k/mo' }
  ];
  return (
    <div className="viz">
      <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet">
        <text x="150" y="20" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1a">Every tool, and what it's worth</text>

        {/* Edges */}
        <g stroke="#1a1a1a" strokeWidth="1.2" strokeOpacity="0.18">
          <line x1="150" y1="105" x2="61" y2="67" />
          <line x1="150" y1="105" x2="239" y2="67" />
          <line x1="150" y1="105" x2="61" y2="165" />
          <line x1="150" y1="105" x2="239" y2="165" />
        </g>

        {/* Pulse ring on hub */}
        <circle cx="150" cy="105" r="24" fill="none" stroke="#9579FC" strokeWidth="1.5" opacity="0">
          <animate attributeName="r" values="24;44;44" dur="3.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.4;0" dur="3.5s" repeatCount="indefinite" />
        </circle>

        {/* Hub */}
        <rect x="108" y="91" width="84" height="28" rx="14" fill="#242422" />
        <text x="150" y="109" textAnchor="middle" fontSize="11" fontWeight="600" fill="#ffffff">The business</text>

        {/* Tool nodes */}
        {tools.map((t, i) => (
          <g key={i}>
            <rect x={t.x} y={t.y} width="74" height="34" rx="7" fill="#ffffff" stroke="#1a1a1a" strokeWidth="1.3" />
            <text x={t.x + 37} y={t.y + 15} textAnchor="middle" fontSize="10" fontWeight="600" fill="#1a1a1a">{t.name}</text>
            <text x={t.x + 37} y={t.y + 27} textAnchor="middle" fontSize="9" fontWeight="600" fill="#9579FC">{t.worth}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const RadarVisual = () => {
  const blips = [
    { cx: 112, cy: 82, r: 6, accent: true, label: '$3.2k' },
    { cx: 198, cy: 96, r: 5, accent: true },
    { cx: 150, cy: 152, r: 4, accent: false },
    { cx: 100, cy: 138, r: 4, accent: false },
    { cx: 186, cy: 146, r: 5, accent: true, label: '$2.4k' }
  ];
  return (
    <div className="viz">
      <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet">
        <text x="150" y="18" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1a">The scored backlog, live</text>

        {/* Rings */}
        <g fill="none" stroke="#1a1a1a" strokeOpacity="0.14" strokeWidth="1">
          <circle cx="150" cy="112" r="30" />
          <circle cx="150" cy="112" r="58" />
          <circle cx="150" cy="112" r="82" />
          <line x1="68" y1="112" x2="232" y2="112" />
          <line x1="150" y1="30" x2="150" y2="194" />
        </g>

        {/* Sweep */}
        <g>
          <animateTransform attributeName="transform" type="rotate" from="0 150 112" to="360 150 112" dur="4s" repeatCount="indefinite" />
          <path d="M150 112 L150 30 A82 82 0 0 1 198 43 Z" fill="#9579FC" fillOpacity="0.14" />
          <line x1="150" y1="112" x2="150" y2="30" stroke="#9579FC" strokeWidth="1.6" strokeOpacity="0.85" />
        </g>

        {/* Blips */}
        {blips.map((b, i) => (
          <g key={i}>
            <circle cx={b.cx} cy={b.cy} r={b.r} fill={b.accent ? '#9579FC' : '#242422'}>
              <animate attributeName="opacity" values="0.3;1;0.3" dur="4s" begin={`${i * 0.8}s`} repeatCount="indefinite" />
            </circle>
            {b.label && (
              <text x={b.cx + b.r + 4} y={b.cy + 3} fontSize="9" fontWeight="600" fill="#1a1a1a">{b.label}</text>
            )}
          </g>
        ))}

        {/* Center */}
        <circle cx="150" cy="112" r="3" fill="#1a1a1a" />

        <text x="150" y="196" textAnchor="middle" fontSize="9.5" fill="#8a857f" fontStyle="italic">Sized by the spend it replaces</text>
      </svg>
    </div>
  );
};

const BuildVisual = () => (
  <div className="viz">
    <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet">
      <text x="150" y="18" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1a1a1a">Ship small. Then displace.</text>

      {/* Build row 1 */}
      <text x="24" y="42" fontSize="10" fontWeight="600" fill="#1a1a1a">Renewal generator</text>
      <rect x="24" y="48" width="212" height="8" rx="4" fill="#E0E0E0" />
      <rect x="24" y="48" width="0" height="8" rx="4" fill="#9579FC">
        <animate attributeName="width" values="0;212;212" keyTimes="0;0.4;1" dur="6s" repeatCount="indefinite" />
      </rect>
      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.4;0.46;1" dur="6s" repeatCount="indefinite" />
        <circle cx="252" cy="52" r="8" fill="#2e9e5b" />
        <path d="M248 52 L251 55 L256 48" stroke="#ffffff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Build row 2 */}
      <text x="24" y="82" fontSize="10" fontWeight="600" fill="#1a1a1a">Onboarding flow</text>
      <rect x="24" y="88" width="212" height="8" rx="4" fill="#E0E0E0" />
      <rect x="24" y="88" width="0" height="8" rx="4" fill="#9579FC">
        <animate attributeName="width" values="0;0;212;212" keyTimes="0;0.25;0.6;1" dur="6s" repeatCount="indefinite" />
      </rect>
      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.6;0.66;1" dur="6s" repeatCount="indefinite" />
        <circle cx="252" cy="92" r="8" fill="#2e9e5b" />
        <path d="M248 92 L251 95 L256 88" stroke="#ffffff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Divider */}
      <line x1="20" y1="118" x2="280" y2="118" stroke="#000000" strokeOpacity="0.08" strokeWidth="1" />
      <text x="24" y="136" fontSize="9" fontWeight="600" fill="#8a857f" letterSpacing="0.5">THEN DISPLACEMENT</text>

      {/* Old vendor tile */}
      <rect x="24" y="144" width="100" height="36" rx="7" fill="#EDE9E5" stroke="#CFC9C1" strokeWidth="1" />
      <text x="74" y="162" textAnchor="middle" fontSize="10" fill="#8a857f">Vendor tool</text>
      <text x="74" y="174" textAnchor="middle" fontSize="8.5" fill="#b8b2ab">$2.4k/mo</text>
      <line x1="30" y1="162" x2="118" y2="162" stroke="#c0392b" strokeWidth="1.6" opacity="0">
        <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.55;0.6;1" dur="6s" repeatCount="indefinite" />
      </line>

      {/* Arrow */}
      <path d="M130 162 L150 162 M144 157 L150 162 L144 167" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />

      {/* New built tile */}
      <rect x="156" y="144" width="120" height="36" rx="7" fill="#242422" />
      <text x="216" y="161" textAnchor="middle" fontSize="10" fontWeight="600" fill="#ffffff">Built for you</text>
      <text x="216" y="173" textAnchor="middle" fontSize="8.5" fill="#9579FC">replaces $2.4k/mo</text>
    </svg>
  </div>
);

const visuals = [WeekVisual, MapVisual, RadarVisual, BuildVisual];

const Features = () => {
  return (
    <section id="features" className="section features">
      <div className="container">
        <div className="features-header">
          <h2 className="section-title">The engine</h2>
          <p className="section-subtitle">
            A team that shows up for a week and stays for years. It starts by learning how the business really works, turns that into a radar of what to build next, and ships: new capability first, displacement second.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => {
            const Visual = visuals[index];
            return (
              <div key={index} className="feature-card">
                <div className="feature-visual">
                  <Visual />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
